/**
 * `<DiceOverlay>` — the global 3D dice surface + result readout. Mounted once at the app root
 * as a peer of `<DirectiveOverlay>` (in `GameSessionProvider`), so a throw tumbles above
 * **every** route — the desktop and the combat surface alike — and above the combat banner
 * (`z-50`). The readout panel is likewise app-wide, so a roll's result is readable from anywhere.
 *
 * **The canvas and its stage are permanent, built once at boot.** The `<canvas>` is mounted for
 * the overlay's whole life; a single {@link DiceStage} is constructed in the build effect below
 * and immediately {@link DiceStage.warmup}'d, so the WebGL context creation and the shader
 * compile are paid behind the loading screen instead of in the click path of someone's turn. Idle
 * costs no CPU — the stage's rAF loop doesn't run until a throw starts it, and `onIdle` stops it
 * again — but it does cost a held GL context, which is the accepted trade. `onIdle` therefore
 * only flips the canvas to `visibility: hidden`; it does **not** dispose anything.
 *
 * **Two GL contexts live on a board page** — Pixi's for the combat surface and this one — against
 * a per-page ceiling of roughly sixteen. Measured with a probe: on the live combat board, with
 * Pixi already holding a working `webgl` renderer, twenty-four further contexts were granted
 * without a refusal, so the second one is not close to any limit. Don't add a *third* casually
 * all the same, and if one ever has to go, the board's is the one worth keeping.
 *
 * The heavy three.js/cannon-es engine is a **static** import, deliberately: the app loads and
 * warms everything it needs before play starts, because a four-hour session pays a bigger
 * bundle exactly once (behind the loading screen, off an `immutable`-cached `/assets/*`) but
 * pays a lazy chunk's fetch + parse + eval *mid-encounter*, in the click path of someone's turn.
 * Nothing here may become a dynamic `import()` again — `noDynamicImport.pin.test.ts` fails the
 * build if it does. The static import is also why the build effect's `try`/`catch` matters: a
 * `DiceStage` constructor that throws (no WebGL context) propagates straight into React rather
 * than being swallowed as an unhandled rejection inside a `.then()`. Losing the dice must never
 * cost the encounter — see the comment at the `catch`.
 *
 * Two request kinds (see {@link diceController}): a dev `d20` (physics-decides, no panel) and a
 * `roll` carrying an authoritative `RollResult` — the overlay choreographs one die per recorded
 * draw onto its face ({@link diceSpecsFromRoll}) and shows the dismissable readout
 * ({@link formatRollSummary}). The result panel outlives the dice (it persists until dismissed
 * or replaced), so the dice can clear while the number stays readable — and it keeps a short
 * **stack** of the most recent rolls rather than one slot, because a Lancer barrage resolves as
 * two separate throws and one slot would let the second overwrite the first shot's total before
 * anyone read it.
 *
 * **Delivering every throw** is the other half — see `DiceOverlay.throwQueue.test.tsx`. Two of
 * the three windows a request could arrive in are closed by construction rather than by machinery:
 *  - the **stage-build window** (a request arriving before a stage exists) is gone on the healthy
 *    path, because the build effect runs during the mounting commit, before `subscribeDiceThrows`
 *    can hand this component anything at all;
 *  - the **teardown window** (a request landing between `onIdle` and a disposal commit, on a
 *    stage whose loop had already stopped) is gone outright: nothing is disposed, and `spawnBatch`
 *    restarts the loop.
 *
 * What survives is the **failure** path. If the boot-time construction throws — the browser
 * refusing a WebGL context — there is no stage, and losing the dice must not cost the encounter
 * (see the comment at the `catch`). A throw arriving then goes into a **queue** and bumps
 * `generation`, which re-keys the `<canvas>` (a `WebGLRenderer` can't be built over an element
 * whose context attempt already failed) and re-runs the build effect, which drains the queue. So
 * a transient context loss self-heals on the next throw, and a barrage's second shot still can't
 * overwrite the first inside that window. `generation` is *not* touched by ordinary rolls: a
 * healthy session builds generation 0 at boot and stays there.
 *
 * **Every throw is awaitable**, and this component is the only thing in the app that knows
 * whether a given throw will actually produce dice. Each request
 * arrives with a {@link ThrowTicket}; the overlay claims it on the one path where dice really
 * reach the stage and a batch id comes back, and registers it in a {@link createBatchLedger}
 * keyed by that id, which `DiceStage`'s `onBatchSettled` then releases. Every other path — no
 * stage, over the visualization cap, a rebuild pending — leaves the ticket unclaimed, and
 * `diceController` releases the caller as soon as the emit returns. The rule is one line: a hold
 * only ever waits on dice that exist.
 */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { tipAttrs } from "../tooltip";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GameSessionContext } from "../game/GameSessionProvider";
import { resolveSlotName } from "../game/useSlotNames";
import { DiceStage } from "./DiceStage";
import { installDiceDevHook, subscribeDiceThrows, type DiceThrowRequest } from "./diceController";
import { projectThrow } from "./throwProjection";
import { createBatchLedger, type ThrowTicket } from "./throwSettlement";
import { rollToastContent, type RollToastContent } from "./rollToast";
import "./DiceOverlay.css";

/** How many recent roll toasts stack at once. Two covers a barrage; three leaves slack. */
const READOUT_DEPTH = 3;
/** How long a toast stays before it leaves by itself, unless hovered. */
export const TOAST_MS = 4000;

type RollToast = RollToastContent & { id: number };

declare global {
  // Module augmentation of the global `Window` requires `interface` (declaration merging) — the same
  // sanctioned exception `CombatStage.tsx`'s `__combatBoardDebug` augmentation takes.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    /**
     * TEST/DIAGNOSTIC ONLY — the seam that makes the boot-warmup claims measurable in a real
     * browser, which is the only tier that can see them (a headless box can't reliably get a WebGL
     * context at all). Read it in the console on a freshly loaded page, **before rolling anything**:
     * `stageReady` true and `programCount()` non-zero means the context was created and the shaders
     * were compiled at boot. `builds` should read 1 for the whole session — a number that climbs
     * with the roll count means the stage is being rebuilt per roll.
     * Mirrors `window.__combatBoardDebug`'s pattern (`CombatStage.tsx`); extend, don't fork.
     */
    __diceStageDebug?: {
      stageReady: boolean;
      builds: number;
      programCount: () => number;
      /**
       * `performance.now()` when a throw's dice most recently STOPPED ROLLING, or `null` before
       * the first one lands. The reveal gate's claim is an ordering — "the outcome reached the
       * screen no earlier than the dice settled" — and both sides of it have to be readable to
       * prove it in a real browser: the dice are a WebGL scene with no DOM, and the board's damage
       * float is drawn by Pixi. Pairs with `__combatBoardDebug.getFloatLog()`.
       */
      lastSettledAt: number | null;
    };
  }
}

export function DiceOverlay() {
  /**
   * Which `<canvas>` element the stage is built over. Bumped **only** when a construction failed
   * and a throw is asking for a retry — a healthy session builds generation 0 at boot and never
   * comes back here. It keys the canvas so a retry gets a genuinely new element rather than one
   * whose context attempt already failed.
   */
  const [generation, setGeneration] = useState(0);
  /** Whether dice are on the table right now — drives the canvas's CSS visibility, nothing else. */
  const [tumbling, setTumbling] = useState(false);
  const [toasts, setToasts] = useState<RollToast[]>([]);
  const nextToastId = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<DiceStage | null>(null);
  const queueRef = useRef<DiceThrowRequest[]>([]);
  /**
   * True from the moment a throw asks for a *rebuild* until the build effect has run — a second
   * throw arriving inside that window rides the rebuild already on its way instead of bumping
   * `generation` again, which would re-key the canvas out from under the first throw's stage.
   * Only ever set on the no-stage failure path.
   */
  const rebuildingRef = useRef(false);
  /** How many stages this overlay has successfully constructed — 1 for a healthy session. */
  const buildsRef = useRef(0);
  /**
   * Who is waiting on which of the stage's batches. Lives across generations even though batch
   * ids restart with each stage, because the build effect's cleanup drains it (`settleAll`)
   * before the next stage hands out id 0 again.
   */
  const ledgerRef = useRef(createBatchLedger());

  // The dev `d20` and a dice-less resolution have no toast; both call sites have already routed
  // the first away, but `rollToastContent`'s return type can't express that, so absorb it here.
  const pushToast = useCallback((req: DiceThrowRequest) => {
    const content = rollToastContent(req);
    if (content === null) return;
    const id = nextToastId.current++;
    setToasts((prev) => [{ ...content, id }, ...prev].slice(0, READOUT_DEPTH));
  }, []);
  const dismissToast = useCallback((id: number) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  // Push a request into the live stage (and surface the readout for a real roll). A throw whose
  // projected dice count exceeds MAX_VISUALIZED_DICE skips the tumble — the readout still shows,
  // `stage.throwSpecs` just never gets the oversized batch.
  // A `null` stage means "the dice engine isn't available this session" (see the build effect):
  // the tumble is dropped, but the readout still fires, because the NUMBER is the part the table
  // actually needs — the dice are the garnish.
  //
  // `ticket` (optional — the queue drain has none) is the caller's wait on this throw. It is
  // claimed on exactly one path: dice really reached the stage and a batch id came back. Every
  // other path leaves it unclaimed, which is what makes `diceController` release the caller the
  // moment the emit returns — no dice, no wait.
  const dispatchToStage = useCallback(
    (stage: DiceStage | null, req: DiceThrowRequest, ticket?: ThrowTicket) => {
      const claimBatch = (batchId: number | null): void => {
        if (ticket === undefined || batchId === null) return;
        ticket.claim();
        ledgerRef.current.register(batchId, ticket);
      };
      if (req.kind === "d20") {
        if (stage === null) return;
        claimBatch(stage.throwDie());
        setTumbling(true);
        return;
      }
      const { specs, visualize } = projectThrow(req);
      if (visualize && stage !== null) {
        claimBatch(stage.throwSpecs(specs!));
        setTumbling(true);
      }
      pushToast(req);
    },
    [pushToast],
  );

  // Build the stage. This effect is declared FIRST so it runs before the subscription effect below
  // during the mounting commit: by the time anything can push a throw at us, the boot-time stage
  // either exists or has already failed. On the healthy path it runs exactly once, at boot, and
  // the cleanup only fires on unmount — a finished roll idles the stage, it does not dispose it.
  useEffect(() => {
    // Clear the "a rebuild is on its way" flag even if the canvas somehow isn't there: leaving it
    // set would wedge the overlay for the rest of the session, since no later throw would ever
    // bump `generation` again. A cleared flag just means the next throw retries the build.
    rebuildingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas === null) return;

    let stage: DiceStage;
    try {
      stage = new DiceStage(canvas, {
        // The table emptied and the loop stopped. Hide the canvas; keep the stage.
        onIdle: () => setTumbling(false),
        // One throw's dice stopped rolling — release whoever was holding that outcome back. Note
        // this is per-batch and fires well before `onIdle`: a barrage's first shot releases while
        // its second is still in the air.
        onBatchSettled: (batchId) => {
          if (window.__diceStageDebug) window.__diceStageDebug.lastSettledAt = performance.now();
          ledgerRef.current.settle(batchId);
        },
      });
      // Pay the shader compile here, at boot, rather than on the first roll. Inside the `try`
      // deliberately: it issues real GL calls, and a driver that refuses them must cost the dice,
      // not the encounter, exactly like the constructor above.
      stage.warmup();
    } catch (error) {
      // `new THREE.WebGLRenderer(...)` THROWS when the browser won't hand out a WebGL context —
      // a GPU driver reset, a software/headless renderer, or simply too many live contexts on the
      // page (Pixi already holds one for the board, and Chrome caps a page at ~16). It has to be
      // caught *here*, synchronously: called straight from the effect, an uncaught throw
      // propagates into React and unmounts the tree — turning "no dice this session" into "the
      // whole encounter UI is gone mid-fight". Measured, not theorized: headless Chrome in
      // the e2e harness gets no WebGL context on the combat board page (GL_VENDOR=Disabled; a bare
      // page does get one), and this exact throw takes the app down there without the catch.
      //
      // So: no dice, but the roll still reads out and the app lives. `stageRef` stays null, which
      // is what routes the NEXT throw back through the rebuild path below — a transient context
      // loss self-heals rather than killing the dice for the rest of the session. Nothing is
      // scheduled here: retrying on a timer would spin forever on a box that simply has no WebGL.
      console.error("DiceOverlay: could not create the dice stage; rolls will read out without dice", error);
      window.__diceStageDebug = { stageReady: false, builds: buildsRef.current, programCount: () => 0, lastSettledAt: null };
      for (const req of queueRef.current.splice(0)) dispatchToStage(null, req);
      return;
    }
    stageRef.current = stage;
    buildsRef.current += 1;
    window.__diceStageDebug = { stageReady: true, builds: buildsRef.current, programCount: () => stage.programCount(), lastSettledAt: null };
    // Only ever non-empty on the rebuild path — a boot build has nothing waiting, and must not
    // invent anything: throwing a default dev `d20` on an empty queue would fling a die across
    // the screen of every player who loaded the app.
    for (const req of queueRef.current.splice(0)) dispatchToStage(stage, req);
    return () => {
      stage.dispose();
      stageRef.current = null;
      // Belt and braces over `dispose`'s own per-batch announcement: whatever the stage did or
      // didn't report on its way out, nobody is left waiting on a stage that no longer exists,
      // and the next stage starts numbering batches from 0 against an empty ledger.
      ledgerRef.current.settleAll();
      delete window.__diceStageDebug;
    };
  }, [generation, dispatchToStage]);

  // Subscribe to throw requests for the overlay's whole lifetime. The healthy path is the first
  // branch and nothing else: a stage exists, so the throw goes straight to it. Everything below it
  // is the no-context fallback — surface the readout for a throw that was never going to tumble,
  // otherwise queue it and ask for a rebuild.
  const onThrow = useCallback(
    (req: DiceThrowRequest, ticket: ThrowTicket) => {
      if (stageRef.current) {
        dispatchToStage(stageRef.current, req, ticket);
        return;
      }
      if (req.kind !== "d20") {
        const projection = projectThrow(req);
        if (!projection.visualize) {
          pushToast(req);
          return;
        }
      }
      // The no-stage path deliberately leaves the ticket UNCLAIMED, so the caller is released
      // immediately rather than made to wait on a rebuild. A rebuild is a failure-recovery path
      // (the browser refused a WebGL context): it may well never happen, since nothing schedules
      // it — only the *next* throw does. Gating an outcome on it would mean a card that never
      // appears. The queued request still tumbles if the rebuild does land; the caller just
      // stopped waiting for it.
      queueRef.current.push(req);
      if (rebuildingRef.current) return;
      rebuildingRef.current = true;
      setGeneration((g) => g + 1);
    },
    [dispatchToStage, pushToast],
  );

  useEffect(() => {
    installDiceDevHook();
    return subscribeDiceThrows(onThrow);
  }, [onThrow]);

  return (
    <>
      {/* Always mounted — the stage binds to this element at boot and a `WebGLRenderer`'s canvas
          can't be swapped afterwards, so it has to be the permanent one from the very first
          render. Idle hides it with `visibility` rather than `display:none`, deliberately: the
          rest of the pipeline (`resize()`, the warmup frame) wants a laid-out, viewport-sized
          element, and a `display:none` canvas has no layout box at all. `key` re-creates the
          element only on the failure-retry path — see `generation`. */}
      <canvas key={generation} ref={canvasRef} className={tumbling ? "lvtt-dice-canvas" : "lvtt-dice-canvas lvtt-dice-idle"} />
      {toasts.length > 0 && <RollToastStack toasts={toasts} tumbling={tumbling} onDismiss={dismissToast} />}
    </>
  );
}

/**
 * The toast stack, newest at the bottom. Every toast holds while dice are still on the table (the
 * number is only worth its {@link TOAST_MS} once the tumble that shows it has landed) and while the
 * stack is hovered; either ending starts each one's wait over, so a toast never vanishes from under
 * the pointer or before its dice stop.
 */
function RollToastStack({ toasts, tumbling, onDismiss }: { toasts: RollToast[]; tumbling: boolean; onDismiss: (id: number) => void }) {
  const [hovered, setHovered] = useState(false);
  const held = hovered || tumbling;
  // Read softly, not through `useSlotNames`: the overlay also mounts with no session around it
  // (its own tests), where a toast simply names nobody.
  const slots = useContext(GameSessionContext)?.slots;
  const names = useMemo(() => new Map((slots ?? []).map((s) => [s.id, s.name])), [slots]);
  const nameOf = (slotId: string) => resolveSlotName(names, slotId);
  return (
    <div className="lvtt-dice-readout" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {toasts.map((toast, i) => (
        <RollToastCard key={toast.id} toast={toast} who={toast.authorSlotId === null ? null : nameOf(toast.authorSlotId)} prior={i > 0} held={held} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function RollToastCard({ toast, who, prior, held, onDismiss }: { toast: RollToast; who: string | null; prior: boolean; held: boolean; onDismiss: (id: number) => void }) {
  useEffect(() => {
    if (held) return;
    const timer = setTimeout(() => onDismiss(toast.id), TOAST_MS);
    return () => clearTimeout(timer);
  }, [held, toast.id, onDismiss]);
  const tone = toast.tone ? ` lvtt-dice-card--${toast.tone}` : "";
  // One band: who/label over the notation on the left, the big total and ✕ on the right. The value
  // element keeps one text run, "<detail> = <total>", so a reader (and a flow) gets the whole roll.
  return (
    <div className={`lvtt-dice-card${prior ? " lvtt-dice-card-prior" : ""}${tone}`} role="status">
      <div className="lvtt-dice-head">
        {who !== null && <span className="lvtt-dice-who">{who}</span>}
        <span className="lvtt-dice-label">{toast.label ?? "ROLL"}</span>
        {toast.tone && <span className="lvtt-dice-tone">{toast.tone === "crit" ? "CRIT" : "MISS"}</span>}
      </div>
      <div className="lvtt-dice-value">
        <span className="lvtt-dice-detail">{toast.detail}</span>
        {toast.total !== null && (
          <>
            <span className="lvtt-dice-eq"> = </span>
            <span className="lvtt-dice-total">{toast.total}</span>
          </>
        )}
      </div>
      <button type="button" aria-label="Dismiss roll result" {...tipAttrs({ label: "Dismiss roll result" })} onClick={() => onDismiss(toast.id)} className="lvtt-dice-dismiss">
        <Icon icon={faXmark} />
      </button>
    </div>
  );
}
